import type { Person } from '../data/model';
import type { SheetOrder } from './reader';

/**
 * A sheet's Courier and Couriers allowed columns (the web's own: only a business has couriers to name), matched to the
 * business's couriers: their whole name, else a first name or the owner's short name only one of them has, else their phone
 * number. Either way the stop keeps the couriers who may take it (a waiting stop's `couriers`): Share out gives it only to
 * them. A name that isn't one of them, or could be two, is flagged on its row and doesn't count.
 */

export interface Named {
  uid: string;
  /** Their own whole name, never the short name: a sheet made elsewhere names them as they are. */
  name: string;
  /** As the business knows them (the short name, else their first name): matched, and what the flags call them. */
  first: string;
  phone?: string | null;
}

/** The business's couriers as a sheet may name them. */
export function namedOf(people: Pick<Person, 'uid' | 'ownName' | 'first' | 'courier'>[]): Named[] {
  return people.map((p) => ({ uid: p.uid, name: p.ownName, first: p.first, phone: p.courier.phone ?? null }));
}

const plain = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/[^a-z0-9]+/g, ' ').trim();
const digits = (s: string) => s.replace(/\D/g, '').slice(-10);

/** The courier [name] is: their uid, 'none' when it isn't one of [people], 'many' when it could be more than one. */
export function courierNamed(name: string, people: Named[]): string | 'none' | 'many' {
  const n = plain(name);
  if (!n) return 'none';
  const one = (xs: Named[]) => (xs.length === 1 ? xs[0].uid : xs.length > 1 ? 'many' : null);
  const whole = one(people.filter((p) => plain(p.name) === n));
  if (whole) return whole;
  const first = one(people.filter((p) => plain(p.first) === n || plain(p.name).split(' ')[0] === n));
  if (first) return first;
  const d = digits(name);
  return (d.length >= 7 && one(people.filter((p) => p.phone && digits(p.phone) === d))) || 'none';
}

const quoted = (xs: string[]) => {
  const q = xs.map((x) => `“${x}”`);
  return q.length === 1 ? q[0] : `${q.slice(0, -1).join(', ')} and ${q.at(-1)}`;
};

/** What's wrong with names that aren't one courier each, in words, and what follows from it. */
function unknownWords(names: { n: string; why: 'none' | 'many' }[], then: string): string {
  const none = names.filter((x) => x.why === 'none').map((x) => x.n);
  const many = names.filter((x) => x.why === 'many').map((x) => x.n);
  const parts = [
    none.length ? `${quoted(none)} ${none.length === 1 ? 'isn’t one of your couriers' : 'aren’t among your couriers'}` : '',
    many.length ? `${quoted(many)} could be more than one of them` : '',
  ].filter(Boolean);
  return `${parts.join(', and ')}, so ${then}.`;
}

/** A row's couriers, and what to say about names that aren't one courier each: the courier named, else those allowed, else anyone (null). */
export function couriersFor(o: Pick<SheetOrder, 'courier' | 'allowed'>, people: Named[]): { couriers: string[] | null; flags: string[] } {
  const firstOf = (uid: string) => people.find((p) => p.uid === uid)?.first ?? 'They';
  const allowed = o.allowed.map((n) => ({ n, uid: courierNamed(n, people) }));
  const found = [...new Set(allowed.map((a) => a.uid).filter((u) => u !== 'none' && u !== 'many'))];
  if (o.courier) {
    const uid = courierNamed(o.courier, people);
    if (uid === 'none' || uid === 'many') return { couriers: null, flags: [unknownWords([{ n: o.courier, why: uid }], 'anyone may take it')] };
    return { couriers: [uid], flags: found.length && !found.includes(uid) ? [`${firstOf(uid)} isn’t among the couriers allowed, so only ${firstOf(uid)} may take it.`] : [] };
  }
  const off = allowed.flatMap((a) => (a.uid === 'none' || a.uid === 'many' ? [{ n: a.n, why: a.uid as 'none' | 'many' }] : []));
  return { couriers: found.length ? found : null, flags: off.length ? [unknownWords(off, found.length ? 'only the others may take it' : 'anyone may take it')] : [] };
}
